import type { Json } from './db/public.types'

export type ChannelLifecycleState = 'waiting_for_client' | 'connected' | 'disconnected'
export type ChannelVisibilityMode = 'owner_only_until_connected' | 'visible_when_connected' | 'hidden'
export type ChannelMembershipType = 'owner' | 'manager' | 'member' | 'viewer'

export interface ChannelByocSettings {
  kind?: string
  lifecycleState?: string
  spawnMode?: string
  visibilityMode?: string
}

export interface ChannelVisibilityInput {
  id: string
  created_by?: string | null
  hidden?: boolean | null
  is_private?: boolean | null
  settings?: Json
}

export interface ChannelSubjectContext {
  userId?: string | null
  accountRole?: string | null
  channelMembership?: ChannelMembershipType | null
}

export interface ChannelVisibilityOptions {
  includeHidden?: boolean
}

const accountOwnerRoles = new Set(['owner', 'admin'])

const membershipRank: Record<ChannelMembershipType, number> = {
  viewer: 1,
  member: 2,
  manager: 3,
  owner: 4
}

function getSettingsRecord(settings: Json | undefined): Record<string, unknown> {
  return settings && typeof settings === 'object' && !Array.isArray(settings)
    ? settings as Record<string, unknown>
    : {}
}

export function getChannelByocSettings(channel: Pick<ChannelVisibilityInput, 'settings'>): ChannelByocSettings | null {
  const settings = getSettingsRecord(channel.settings)
  const byoc = settings.byoc

  if (!byoc || typeof byoc !== 'object' || Array.isArray(byoc)) {
    return null
  }

  return byoc as ChannelByocSettings
}

export function isByocChannel(channel: Pick<ChannelVisibilityInput, 'settings'>): boolean {
  const byoc = getChannelByocSettings(channel)
  return byoc?.kind === 'byoc' || byoc?.spawnMode === 'byoc_only'
}

export function getByocLifecycleState(channel: Pick<ChannelVisibilityInput, 'settings'>): ChannelLifecycleState {
  const lifecycleState = getChannelByocSettings(channel)?.lifecycleState
  return lifecycleState === 'connected' || lifecycleState === 'disconnected' || lifecycleState === 'waiting_for_client'
    ? lifecycleState
    : 'waiting_for_client'
}

export function getByocVisibilityMode(channel: Pick<ChannelVisibilityInput, 'settings'>): ChannelVisibilityMode {
  const visibilityMode = getChannelByocSettings(channel)?.visibilityMode
  return visibilityMode === 'visible_when_connected' || visibilityMode === 'hidden' || visibilityMode === 'owner_only_until_connected'
    ? visibilityMode
    : 'owner_only_until_connected'
}

export function isByocOnlyChannel(channel: Pick<ChannelVisibilityInput, 'settings'>): boolean {
  return getChannelByocSettings(channel)?.spawnMode === 'byoc_only'
}

export function isActiveAccountOwnerOrAdmin(accountRole?: string | null): boolean {
  return Boolean(accountRole && accountOwnerRoles.has(accountRole))
}

export function resolveAccountChannelMembership(accountRole?: string | null): ChannelMembershipType | null {
  if (!accountRole) return null

  if (isActiveAccountOwnerOrAdmin(accountRole)) {
    return 'owner'
  }

  return 'member'
}

export function canViewHiddenChannel(
  channel: Pick<ChannelVisibilityInput, 'created_by'>,
  subject: ChannelSubjectContext
): boolean {
  return Boolean(
    subject.userId && channel.created_by === subject.userId
  ) || isActiveAccountOwnerOrAdmin(subject.accountRole)
}

export function resolveChannelMembership(
  channel: Pick<ChannelVisibilityInput, 'created_by'>,
  subject: ChannelSubjectContext
): ChannelMembershipType | null {
  const candidates: ChannelMembershipType[] = []

  if (subject.channelMembership) {
    candidates.push(subject.channelMembership)
  }

  const accountMembership = resolveAccountChannelMembership(subject.accountRole)
  if (accountMembership) {
    candidates.push(accountMembership)
  }

  if (subject.userId && channel.created_by === subject.userId) {
    candidates.push('owner')
  }

  return candidates.sort((a, b) => membershipRank[b] - membershipRank[a])[0] || null
}

export function hasMinimumChannelMembership(
  membership: ChannelMembershipType | null | undefined,
  minimum: ChannelMembershipType
): boolean {
  return Boolean(membership && membershipRank[membership] >= membershipRank[minimum])
}

export function canViewChannel(
  channel: ChannelVisibilityInput,
  subject: ChannelSubjectContext,
  options: ChannelVisibilityOptions = {}
): boolean {
  if (channel.hidden && !options.includeHidden && !canViewHiddenChannel(channel, subject)) {
    return false
  }

  if (!isByocChannel(channel)) {
    return true
  }

  const lifecycleState = getByocLifecycleState(channel)
  const visibilityMode = getByocVisibilityMode(channel)
  const membership = resolveChannelMembership(channel, subject)

  if (visibilityMode === 'hidden') {
    return canViewHiddenChannel(channel, subject)
  }

  if (visibilityMode === 'visible_when_connected') {
    return lifecycleState === 'connected' && hasMinimumChannelMembership(membership, 'viewer')
  }

  if (lifecycleState === 'connected') {
    return hasMinimumChannelMembership(membership, 'viewer')
  }

  return hasMinimumChannelMembership(membership, 'manager')
}

export function filterVisibleChannels<T extends ChannelVisibilityInput>(
  channels: T[],
  subject: ChannelSubjectContext,
  options: ChannelVisibilityOptions = {}
): T[] {
  return channels.filter((channel) => canViewChannel(channel, subject, options))
}

export function getByocStatusLabel(channel: Pick<ChannelVisibilityInput, 'settings'>): string {
  if (!isByocChannel(channel)) return ''

  const lifecycleState = getByocLifecycleState(channel)

  if (lifecycleState === 'connected') {
    return 'BYOM Connected'
  }

  if (lifecycleState === 'disconnected') {
    return 'BYOM Offline'
  }

  return 'BYOM Waiting for Connection'
}

export function shouldShowHostedChannelControls(channel: Pick<ChannelVisibilityInput, 'settings'>): boolean {
  return !isByocChannel(channel) && !isByocOnlyChannel(channel)
}
